import axios from 'axios';
import Pusher from 'pusher-js';

import { User } from './User';
import { Board } from './Board';
import { API_URL, PUSHER_AUTH_ENDPOINT, PUSHER_CLUSTER, PUSHER_KEY } from '../../config';
import { isSedeMode } from '../../../../utils/partnerAccess/partnerStorage';

// Partner access: the gateway to the boards of a sede. Loaded on demand: only
// a tab working a sede needs it, and the partner client stays out of the
// module graph of the normal boards.
const loadSedeBoardsApi = () => import('../../../../utils/boards/sedeBoardsApi');

// Partner access: the boards Pusher client authenticates against the boards
// service with the normal JWT, and that service publishes the whole token
// payload as presence data. A tab working a sede gets no such client: its
// boards open without live updates until realtime goes through the partner
// route.
const boardsPusherToken = () => {
  if (isSedeMode()) return null;
  return localStorage ? localStorage.getItem('token') : null;
};

export class AppState {
  _user = new User();
  _pusher = null;
  _boards = [];
  dispatch = undefined;

  constructor(
    user,
    boards,
    pusher,
    dispatch
  ) {
    const token = boardsPusherToken();
    this._boards = boards || [];
    this._user = user || new User();
    this._pusher = pusher || (token ? new Pusher(`${PUSHER_KEY}`, {
      cluster: `${PUSHER_CLUSTER}`,
      forceTLS: true,
      channelAuthorization: {
        endpoint: `${PUSHER_AUTH_ENDPOINT}`,
        transport: 'ajax',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    }) : null);
    this.dispatch = dispatch;
    this.getBoardsFromApi = this.getBoardsFromApi.bind(this);
    this.getBoardFromApi = this.getBoardFromApi.bind(this);
    this.getBoardsPaginated = this.getBoardsPaginated.bind(this);
    this.updateLocalBoard = this.updateLocalBoard.bind(this);
    this.setSedeCardAmount = this.setSedeCardAmount.bind(this);
    this.holdSedeBoardAnswer = this.holdSedeBoardAnswer.bind(this);
  }

  get user() {
    return this._user;
  }

  get pusher() {
    return this._pusher;
  }

  get boards() {
    return this._boards;
  }

  set boards(boards) {
    this._boards = boards;
  }

  set user(user) {
    this._user = user;
  }

  static fromJson(json) {
    const user = User.fromJson(json.user);
    const boards = json.boards.map((board) => Board.fromJson(board));
    return new AppState(user, boards, null, json.dispatch);
  }

  toJson() {
    return {
      user: this._user.toJson(),
      boards: this._boards.map((board) => board.toJson()),
      dispatch: this.dispatch,
    };
  }

  async getBoardsFromApi() {
    if (isSedeMode()) return (await this.getSedeBoardSummaries()).boards;
    const url = this._user.companyId ? `${API_URL}/boards/user/${this._user.id}/company/${this._user.companyId}` : `${API_URL}/boards/user/${this._user.id}`;
    const boards = await axios({
      url: this._user?.rol?.name === 'superadmin' ? `${API_URL}/boards/company/${this._user.companyId}` : url,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
    }).then(({ data }) => {
      const boards = data.map((board) => Board.fromJson(board));
      return boards;
    });

    this._boards = boards;
    this.dispatch &&  this.dispatch({ type: 'UPDATE_STATE' });

    return boards;
  }

  async getBoardsPaginated({ page = 1, limit = 20, search = '', tab = 'all' } = {}) {
    if (isSedeMode()) return this.getSedeBoardsPage();
    if (!this._user.companyId || !this._user.id) {
      return { boards: [], pagination: { total: 0, hasMore: false } };
    }

    const params = new URLSearchParams({ page, limit, search, tab });
    const { data } = await axios({
      url: `${API_URL}/boards/user/${this._user.id}/company/${this._user.companyId}/paginated?${params}`,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
    });

    const boards = data.boards.map((board) => Board.fromJson(board));
    return { boards, pagination: data.pagination };
  }

  boardsToJSON() {
    return this._boards.map((board) => board.toJson());
  }

  async getBoardFromApi(id) {
    if (isSedeMode()) return this.getSedeBoard(id);
    const board = await axios({
      url: `${API_URL}/boards/${id}`,
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
    }).then(({ data }) => {
      return Board.fromJson(data);
    });

    if (!board) {
      return;
    }

    if (this._boards.find((item) => board.id === item.id)) {
      this._boards = this._boards.map((b) => (b.id === board.id ? board : b));
      this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
      return board;
    }

    this._boards.push(board);
    this.dispatch &&  this.dispatch({ type: 'UPDATE_STATE' });

    return board;
  }

  async createBoard(board) {
    const newBoard = await axios({
      url: `${API_URL}/boards`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
      data: board,
    }).then(({ data }) => {
      return Board.fromJson(data);
    });

    this._boards.push(newBoard);
    this.dispatch({ type: 'UPDATE_STATE' });
  }

  async updateBoard(board) {
    const updatedBoard = await axios({
      url: `${API_URL}/boards/${board.id}`,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
      data: board,
    }).then(({ data }) => {
      return Board.fromJson(data);
    });

    const index = this._boards.findIndex((b) => b.id === updatedBoard.id);
    this._boards[index] = updatedBoard;
    this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
  }

  async deleteBoard(id) {
    await axios({
      url: `${API_URL}/boards/${id}`,
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this._user.token}`,
      },
    });

    this._boards = this._boards.filter((board) => board.id !== id);
    this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
  }

  updateLocalBoard(board) {
    if (!board) {
      return;
    }
    // `_id ?? id`: Board.fromJson acepta las dos formas; buscar solo por `_id` perdia el refresco en
    // silencio si el servidor respondiera `id`.
    const boardId = board._id ?? board.id;
    if (!this._boards.some((b) => b.id === boardId)) {
      return;
    }
    // Array NUEVO, no mutacion en sitio: el efecto de views/Board/index.jsx depende de la identidad
    // de `boards`, y sin ella no hay setBoard ni se recalcula boardsFormatted (al renombrar una
    // columna, `containers` se quedaba con el nombre viejo). Mismo patron que getBoardFromApi.
    const updated = Board.fromJson(board);
    this._boards = this._boards.map((b) => (b.id === boardId ? updated : b));
    this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
    return updated;
  }

  // -------------------------------------------------------------------------
  // Partner access: inside a sede the boards are read and written through the
  // partner routes (utils/boards/sedeBoardsApi.js), never the boards service.
  // A failure rejects with the error of that gateway and leaves the boards
  // held here as they were; the screens decide what to say.
  // -------------------------------------------------------------------------

  /**
   * The list of a sede carries summaries: columns without their cards. They
   * are handed out and never stored: `_boards` only holds boards read in full
   * (getSedeBoard), or the open board would lose its cards on screen.
   */
  async getSedeBoardSummaries() {
    const { listBoards } = await loadSedeBoardsApi();
    const { boards, truncated } = await listBoards();
    return { boards: boards.map((board) => Board.fromJson(board)), truncated };
  }

  /**
   * A sede has no server pagination or search: everything the partner route
   * gives, as the single page of the list. The screen filters what it has.
   */
  async getSedeBoardsPage() {
    const { boards, truncated } = await this.getSedeBoardSummaries();
    return { boards, pagination: { total: boards.length, hasMore: false, page: 1 }, truncated };
  }

  async getSedeBoard(id) {
    const { getBoard } = await loadSedeBoardsApi();
    return this.holdSedeBoard(Board.fromJson(await getBoard(id)));
  }

  /**
   * `boards` with `board` in place of the one with its id, or after the last
   * one. A new array, as in updateLocalBoard: views/Board depends on its
   * identity.
   */
  static withBoard(boards, board) {
    return boards.some((item) => item.id === board.id)
      ? boards.map((item) => (item.id === board.id ? board : item))
      : [...boards, board];
  }

  /**
   * Holds a board read in full. A partner route can take seconds, and the
   * context rebuilds the state on every change: by the time the board is
   * here, `this` may no longer be the state on screen. So the board travels
   * in the action and the reducer holds it in the state of NOW
   * (context/reducers); `UPDATE_STATE` would rebuild that state from its own
   * boards and lose this one.
   */
  holdSedeBoard(board) {
    this._boards = AppState.withBoard(this._boards, board);
    this.dispatch && this.dispatch({ type: 'HOLD_SEDE_BOARD', board });

    return board;
  }

  /** Holds the raw board a write answered, as the drag service hands it over. */
  holdSedeBoardAnswer(raw) {
    return this.holdSedeBoard(Board.fromJson(raw));
  }

  /**
   * `fields` is `{ name, description, guests }`. The new board is handed out
   * and not held: the screen opens it, and opening reads it.
   */
  async createSedeBoard(fields) {
    const { createBoard } = await loadSedeBoardsApi();
    return Board.fromJson(await createBoard(fields));
  }

  /**
   * Replaces the name, the description and the guests of a board. The list
   * works on summaries, so the board is refreshed only where it is already
   * held in full.
   */
  async updateSedeBoard(id, fields) {
    const { updateBoard } = await loadSedeBoardsApi();
    const raw = await updateBoard(id, fields);
    this.updateLocalBoard(raw);
    return Board.fromJson(raw);
  }

  async deleteSedeBoard(id) {
    const { deleteBoard } = await loadSedeBoardsApi();
    await deleteBoard(id);

    if (this._boards.some((board) => board.id === id)) {
      this._boards = this._boards.filter((board) => board.id !== id);
      this.dispatch && this.dispatch({ type: 'UPDATE_STATE' });
    }
  }

  /** The route answers the whole board after the change: it is held as read. */
  async setSedeCardAmount(boardId, clientId, amount) {
    const { setCardAmount } = await loadSedeBoardsApi();
    return this.holdSedeBoard(Board.fromJson(await setCardAmount(boardId, clientId, amount)));
  }
}
